/* eslint-disable react/no-danger */
import React, { useContext } from 'react';
import styled from 'styled-components';
import { SEO, Testimonials, Form, CartItem, Map } from 'components';
import { graphql } from 'gatsby';
import DOMPurify from 'isomorphic-dompurify';
import { LocalContext } from 'context';
import { getAltTagFromFileName } from 'utils';
import { Hero } from 'components/ProductPage/Shared';
import { GatsbyImage } from 'gatsby-plugin-image';
import { AnimatePresence, motion } from 'framer-motion';

function RequestAQuote({ data }) {
  const { quote } = useContext(LocalContext);
  const { heroData, stairsWithBlackBannistersImage } = data;
  const { heroImage, heroTitle, heroDescription } = heroData.frontmatter;
  return (
    <>
      <Hero
        height="14.75rem"
        heroImage={heroImage}
        heroImageOffset={-162}
        filter="contrast(130%) saturate(115%)"
        gradientPercentage="80%"
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Request A Quote"
        breadcrumb="Request A Quote"
        removeProductNameFromPath>
        <h1
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroTitle) }}
          style={{ fontSize: '2.5rem' }}
        />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <Grid>
        <InnerGrid className="fade-in">
          <AnimatePresence>
            {quote.length && (
              <Cart>
                <ul>
                  <AnimatePresence mode="popLayout">
                    {quote.map((item) => (
                      <CartItem key={item.cartEntryId} item={item} />
                    ))}
                  </AnimatePresence>
                </ul>
              </Cart>
            )}
            {!quote.length && (
              <NothingInCart>
                <GatsbyImage
                  image={stairsWithBlackBannistersImage.childImageSharp.gatsbyImageData}
                  alt={getAltTagFromFileName(stairsWithBlackBannistersImage.name)}
                />
              </NothingInCart>
            )}
          </AnimatePresence>
          <StyledForm type="Request A Quote" />
        </InnerGrid>
      </Grid>
      <Map />
      <StyledTestimonials />
    </>
  );
}

const StyledForm = styled(Form)`
  grid-column: 1/-1;
  grid-row: 2/3;
  margin-top: 2.5rem;
  min-height: fit-content;
  padding: 0;
  position: relative;
  width: 100%;

  @media (width >= 1200px) {
    grid-column: 6/-1;
    grid-row: 1/3;
    margin-top: initial;
    padding: 0 1rem;
  }

  @media (width >= 1350px) {
    padding: 0 20px 0 3.125rem;
  }
`;

const Grid = styled.section`
  background-color: var(--connolly-grey);
  display: grid;
  grid-column-gap: 1.25rem;
  grid-template-columns: repeat(12, 1fr);
  margin: 0 auto;
  max-width: 90rem;
  padding: 0;

  @media (width >= 590px) {
    padding: 0 0.625rem;
  }
`;

const InnerGrid = styled.div`
  background-color: #fff;
  border-radius: 0.375rem;
  grid-column: 1/-1;
  grid-template-columns: subgrid;
  grid-template-rows: auto 1fr;
  margin: 0 auto;
  max-width: 590px;
  padding: 1.25rem 1.25rem 2.5rem;
  position: relative;
  row-gap: 1rem;
  top: 0;
  width: 100%;
  z-index: 1;

  @media (width >= 590px) {
    padding: 1.25rem 1.25rem 2.5rem;
  }

  @media (width >= 650px) {
    top: -2.313rem;
  }

  @media (width >= 900px) {
    padding: 1.875rem 1.875rem 2.5rem;
  }

  @media (width >= 1200px) {
    margin: initial;
    display: grid;
    grid-column: 2/12;
    max-width: initial;
  }
`;

const Cart = styled(motion.section).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
})`
  background-color: var(--connolly-grey);
  border: 1px solid #c4c4c4;
  border-radius: 6px;
  grid-column: 1/-1;
  grid-row: 1/2;
  height: auto;
  padding: 1.25rem;

  @media (width >= 1200px) {
    grid-column: 1/6;
    grid-row: 1/3;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
`;

const NothingInCart = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
})`
  grid-column: 1/6;
  grid-row: 1/3;
`;

const StyledTestimonials = styled(Testimonials)`
  > h3 {
    padding: 3.125em 0 1.25em;
  }
`;

export const query = graphql`
  query {
    heroData: markdownRemark(frontmatter: { title: { eq: "Request A Quote" } }) {
      id
      frontmatter {
        heroTitle
        heroDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
    stairsWithBlackBannistersImage: file(
      relativePath: { eq: "shared/stairs-with-black-bannisters.png" }
    ) {
      name
      childImageSharp {
        gatsbyImageData(width: 550, height: 848, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="Request a Quote | Connolly Stairs & Doors Ireland"
      description="Get a personalized quote from Connolly Stairs & Doors. Submit your project details easily and receive competitive pricing quickly.">
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Stairs Ireland", "item": "https://www.connollystairs.ie/" },
          { "@type": "ListItem", "position": 2, "name": "Request a Quote", "item": "https://www.connollystairs.ie/request-a-quote/" }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default RequestAQuote;
